import { useState } from 'react';
import type { PromptTrace } from '../../api/types';

interface Props {
  prompts: PromptTrace[];
}

function PromptCard({ trace }: { trace: PromptTrace }) {
  const [expanded, setExpanded] = useState(false);

  const serviceColor = trace.service === 'claude' ? 'bg-amber-500' : 'bg-green-500';
  const serviceName = trace.service === 'claude' ? 'Claude' : 'OpenAI';

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`${serviceColor} text-white text-xs px-2 py-0.5 rounded`}>
            {serviceName}
          </span>
          <span className="text-sm font-medium capitalize">{trace.role}</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>{trace.latencyMs}ms</span>
          {trace.tokensInput != null && (
            <span>
              {trace.tokensInput} → {trace.tokensOutput} tokens
            </span>
          )}
          <span className="text-xl">{expanded ? '−' : '+'}</span>
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t">
          {/* Prompt */}
          <div className="p-4 bg-gray-50">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Prompt
            </div>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto font-mono bg-white rounded p-3 border">
              {trace.prompt}
            </pre>
          </div>

          {/* Response */}
          <div className="p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Response
            </div>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto font-mono bg-green-50 rounded p-3 border border-green-100">
              {trace.response}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PromptInspector({ prompts }: Props) {
  if (prompts.length === 0) return null;

  const totalTokens = prompts.reduce(
    (sum, p) => ({
      input: sum.input + (p.tokensInput || 0),
      output: sum.output + (p.tokensOutput || 0),
    }),
    { input: 0, output: 0 }
  );

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Prompt Inspector</h3>
        <div className="text-sm text-gray-500">
          Total: {totalTokens.input} input + {totalTokens.output} output tokens
        </div>
      </div>

      <div className="space-y-3">
        {prompts.map((trace, i) => (
          <PromptCard key={i} trace={trace} />
        ))}
      </div>
    </div>
  );
}
